import { ref, computed, type Ref } from 'vue';
import { defineStore } from 'pinia';
import type { TenantConfig, FeatureFlags, ThemeConfig } from '@/tenant/types/tenant';
import { fetchTenantConfig } from '@/tenant/services/tenantService';

const DEFAULT_THEME: ThemeConfig = {
  primary: '#00A89D',
  secondary: '#F5F5F5',
  accent: '#FF6B35',
  textPrimary: '#1F2937',
  textSecondary: '#6B7280',
  borderRadius: '0.75rem',
};

const DEFAULT_FEATURES: FeatureFlags = {
  appointments: true,
  inventory: true,
  billing: true,
  reports: true,
  medicalRecords: true,
  prescriptions: true,
  heats: true,
  vaccinations: true,
  formats: true,
  multiLanguage: false,
};

export const useTenantStore = defineStore('tenant', () => {
  const loading = ref(false);
  const error: Ref<unknown> = ref(null);
  const config = ref<TenantConfig | null>(null);

  const theme = computed<ThemeConfig>(() => config.value?.theme ?? DEFAULT_THEME);
  const features = computed<FeatureFlags>(() => config.value?.features ?? DEFAULT_FEATURES);
  const tenantName = computed(() => config.value?.name ?? '');
  const tenantLogo = computed(() => config.value?.logo ?? '');
  const plan = computed(() => config.value?.plan ?? 'basic');

  const isFeatureEnabled = (feature: keyof FeatureFlags): boolean => {
    return features.value[feature] ?? false;
  };

  const loadTenantConfig = async (tenantId: string) => {
    loading.value = true;
    error.value = null;
    try {
      config.value = await fetchTenantConfig(tenantId);
    } catch (e) {
      error.value = e;
      throw e;
    } finally {
      loading.value = false;
    }
  };

  const clear = () => {
    config.value = null;
    error.value = null;
  };

  return {
    loading,
    error,
    config,
    theme,
    features,
    tenantName,
    tenantLogo,
    plan,
    isFeatureEnabled,
    loadTenantConfig,
    clear,
  };
});
